import React, { useState, useEffect } from 'react';
import { 
  HardDrive, Trash2, RefreshCw, AlertTriangle, Image as ImageIcon, Video, FileText, 
  Search, ShieldAlert, CheckCircle, Database, Server, Filter, ExternalLink, Play, X, Download
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const StorageManagement = ({ orgData, showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL'); // 'ALL', 'PHOTO', 'VIDEO'
  const [deletingId, setDeletingId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  useEffect(() => {
    fetchStorageData();
  }, []);

  const getAuthToken = () => {
    let token = localStorage.getItem('authToken') || localStorage.getItem('jwtToken') || localStorage.getItem('token') || '';
    if (token.toLowerCase().startsWith('bearer ')) {
      token = token.substring(7).trim();
    }
    return token;
  };

  const getFullMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const token = getAuthToken();
    const separator = url.includes('?') ? '&' : '?';
    return `${API_BASE_URL}${url}${url.includes('token=') ? '' : `${separator}token=${encodeURIComponent(token)}`}`;
  };

  const fetchStorageData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      // 1. Fetch storage stats
      const statsRes = await fetch(`${API_BASE_URL}/api/admin/storage/dashboard`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // 2. Fetch media list
      const mediaRes = await fetch(`${API_BASE_URL}/api/admin/storage/media-list`, { headers });
      if (mediaRes.ok) {
        const mediaData = await mediaRes.json();
        setActivities(mediaData.activities || []);
      }
    } catch (err) {
      console.error('Failed to load storage management data:', err);
      if (showNotification) showNotification('Failed to load storage dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/storage/reconcile`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (showNotification) showNotification(`Storage reconciled! Current usage: ${data.reconciledUsedMb}`, 'success');
        fetchStorageData();
      }
    } catch (err) {
      if (showNotification) showNotification('Reconcile failed', 'error');
    } finally {
      setReconciling(false);
    }
  };

  const handleDeleteMedia = async (mediaId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete file "${fileName}"? This will permanently remove it from storage and free up space.`)) return;

    setDeletingId(`media_${mediaId}`);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/storage/media/${mediaId}?token=${encodeURIComponent(token)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (showNotification) showNotification(`Media deleted! Freed ${formatBytes(data.freedBytes)}.`, 'success');
        fetchStorageData();
      } else {
        const errData = await res.json();
        if (showNotification) showNotification(errData.error || 'Delete failed', 'error');
      }
    } catch (err) {
      if (showNotification) showNotification('Failed to delete media file', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteActivity = async (activityId, title) => {
    if (!window.confirm(`Are you sure you want to delete activity "${title}" and all its attached media? This cannot be undone.`)) return;

    setDeletingId(`act_${activityId}`);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/storage/activity/${activityId}?token=${encodeURIComponent(token)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (showNotification) showNotification(`Activity & attachments purged! Freed ${formatBytes(data.freedBytes)}.`, 'success');
        fetchStorageData();
      } else {
        const errData = await res.json();
        if (showNotification) showNotification(errData.error || 'Delete failed', 'error');
      }
    } catch (err) {
      if (showNotification) showNotification('Failed to delete activity', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadMedia = async (m) => {
    if (!m || !m.url) return;
    const fileName = m.originalName || m.fileName || 'media_file';
    const downloadUrl = getFullMediaUrl(m.url) + `&download=true&filename=${encodeURIComponent(fileName)}`;

    try {
      if (showNotification) showNotification('Starting download...', 'info');
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      if (showNotification) showNotification('Download started successfully!', 'success');
    } catch (err) {
      console.error('Blob download error, opening download URL directly:', err);
      window.open(downloadUrl, '_blank');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Filter activities
  const filteredActivities = activities.filter(act => {
    const matchesSearch = (act.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (act.teacherName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (act.className || '').toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;
    if (filterType === 'ALL') return true;

    return (act.media || []).some(m => (m.mediaType || '').toUpperCase() === filterType);
  });

  const usagePercent = stats ? stats.usagePercent || 0 : 0;
  const isWarning = stats ? stats.warningFlag || usagePercent > 85 : false;

  return (
    <div style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Header Banner */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <HardDrive style={{ color: '#2563eb' }} size={28} />
            Media & Storage Management
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
            Monitor organization storage quota, inspect uploaded media, and clear storage space.
          </p>
        </div>
        <button
          onClick={handleReconcile}
          disabled={reconciling}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px',
            backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px',
            color: '#334155', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s'
          }}
        >
          <RefreshCw size={16} className={reconciling ? 'spin' : ''} />
          {reconciling ? 'Reconciling...' : 'Recalculate Storage'}
        </button>
      </div>

      {/* Storage Gauge Card */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '16px', padding: '24px', color: '#ffffff', marginBottom: '24px',
        boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
              Storage Usage
            </span>
            <div style={{ fontSize: '32px', fontWeight: 800, marginTop: '4px' }}>
              {stats ? formatBytes(stats.totalUsedBytes) : 'Loading...'}
              <span style={{ fontSize: '16px', color: '#64748b', fontWeight: 400, marginLeft: '8px' }}>
                / {stats ? stats.limitGb : '50.00 GB'} Limit
              </span>
            </div>
          </div>

          {isWarning && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#7f1d1d', border: '1px solid #ef4444', padding: '8px 14px', borderRadius: '30px', color: '#fca5a5', fontSize: '13px', fontWeight: 600 }}>
              <AlertTriangle size={16} />
              Storage Warning: Near Capacity ({usagePercent.toFixed(1)}%)
            </div>
          )}
        </div>

        {/* Meter Bar */}
        <div style={{ background: '#334155', height: '12px', borderRadius: '6px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{
            width: `${Math.min(100, Math.max(1, usagePercent))}%`,
            height: '100%',
            background: isWarning
              ? 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)'
              : 'linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)',
            transition: 'width 0.5s ease-in-out'
          }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', paddingTop: '12px', borderTop: '1px solid #334155' }}>
          <div>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Capacity Used</span>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>{usagePercent.toFixed(1)}%</div>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Total Activities</span>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>{activities.length} Events</div>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Auto Cleanup</span>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>{stats?.retentionDays || 365} Days</div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search by title, teacher, or class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 38px', borderRadius: '10px',
              border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          {['ALL', 'PHOTO', 'VIDEO'].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                border: '1px solid', cursor: 'pointer',
                borderColor: filterType === t ? '#2563eb' : '#cbd5e1',
                backgroundColor: filterType === t ? '#eff6ff' : '#ffffff',
                color: filterType === t ? '#2563eb' : '#64748b'
              }}
            >
              {t === 'ALL' ? 'All Media' : t === 'PHOTO' ? 'Photos Only' : 'Videos Only'}
            </button>
          ))}
        </div>
      </div>

      {/* Activity Media Explorer Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <RefreshCw size={32} className="spin" style={{ marginBottom: '12px' }} />
          <p>Loading activity storage metrics...</p>
        </div>
      ) : filteredActivities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
          <Database size={40} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
          <h3 style={{ margin: 0, fontSize: '16px', color: '#334155' }}>No media activities found</h3>
          <p style={{ fontSize: '13px', margin: '4px 0 0' }}>No uploaded media matches your filter criteria.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredActivities.map((act) => (
            <div key={act.activityId} style={{
              backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0',
              padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              {/* Activity Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      backgroundColor: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: 700,
                      padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase'
                    }}>
                      {act.activityType}
                    </span>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Class: {act.className} {act.section}</span>
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>• {act.createdAt?.split('T')[0]}</span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{act.title}</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Teacher: {act.teacherName}</p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155', backgroundColor: '#f1f5f9', padding: '6px 12px', borderRadius: '8px' }}>
                    Total: {act.totalSizeMbFormatted} ({act.totalMediaCount} files)
                  </span>
                  <button
                    onClick={() => handleDeleteActivity(act.activityId, act.title)}
                    disabled={deletingId === `act_${act.activityId}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
                      backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
                      borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={14} />
                    {deletingId === `act_${act.activityId}` ? 'Purging...' : 'Delete Activity'}
                  </button>
                </div>
              </div>

              {/* Media Attachments Grid */}
              {act.media && act.media.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                  {act.media.map((m) => {
                    const mediaFullUrl = getFullMediaUrl(m.url);
                    const thumbFullUrl = getFullMediaUrl(m.thumbnailUrl);
                    const isVid = (m.mediaType || '').toUpperCase() === 'VIDEO';

                    return (
                      <div key={m.id} style={{
                        display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', borderRadius: '8px',
                        border: '1px solid #e2e8f0', overflow: 'hidden'
                      }}>
                        {/* Preview Thumbnail / Video Frame */}
                        <div 
                          onClick={() => setPreviewMedia({ ...m, activityTitle: act.title })}
                          style={{
                            height: '130px', backgroundColor: '#0f172a', position: 'relative',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden'
                          }}
                        >
                          {isVid ? (
                            thumbFullUrl ? (
                              <img
                                src={thumbFullUrl}
                                alt={m.originalName || m.fileName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <video
                                src={`${mediaFullUrl}#t=0.1`}
                                muted
                                playsInline
                                preload="metadata"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            )
                          ) : (
                            <img
                              src={mediaFullUrl}
                              alt={m.originalName || m.fileName}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )}

                          {/* Play Icon / Type Badge Overlay */}
                          <div style={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(15, 23, 42, 0.75)', borderRadius: '50%', width: '42px', height: '42px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          }}>
                            {isVid ? <Play size={20} style={{ marginLeft: '3px' }} /> : <ImageIcon size={20} />}
                          </div>

                          <div style={{ position: 'absolute', top: '8px', left: '8px', backgroundColor: 'rgba(15,23,42,0.85)', color: '#ffffff', padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {isVid ? <Video size={12} /> : <ImageIcon size={12} />}
                            {m.mediaType}
                          </div>
                        </div>

                        {/* File Details */}
                        <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155', wordBreak: 'break-all', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {m.originalName || m.fileName}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                              Size: {formatBytes(m.fileSizeBytes)}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                            <button
                              onClick={() => setPreviewMedia({ ...m, activityTitle: act.title })}
                              style={{
                                flex: 1, textAlign: 'center', padding: '6px', backgroundColor: '#eff6ff',
                                border: '1px solid #bfdbfe', borderRadius: '6px', color: '#2563eb',
                                fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                              }}
                            >
                              <Play size={12} /> {isVid ? 'Play Video' : 'View Image'}
                            </button>
                            <button
                              onClick={() => handleDeleteMedia(m.id, m.originalName || m.fileName)}
                              disabled={deletingId === `media_${m.id}`}
                              style={{
                                padding: '6px 10px', backgroundColor: '#ffffff', border: '1px solid #fecaca',
                                color: '#dc2626', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer'
                              }}
                            >
                              {deletingId === `media_${m.id}` ? '...' : <Trash2 size={12} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── INTERACTIVE MEDIA PLAYER & PHOTO VIEWER MODAL ── */}
      {previewMedia && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(6px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#0f172a', borderRadius: '16px', border: '1px solid #334155',
            width: '100%', maxWidth: '840px', overflow: 'hidden', color: '#ffffff',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Modal Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>
                  {previewMedia.originalName || previewMedia.fileName}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                  {previewMedia.activityTitle} • {formatBytes(previewMedia.fileSizeBytes)}
                </p>
              </div>
              <button
                onClick={() => setPreviewMedia(null)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Player Content */}
            <div style={{ padding: '20px', backgroundColor: '#020617', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
              {(previewMedia.mediaType || '').toUpperCase() === 'VIDEO' ? (
                <video
                  src={getFullMediaUrl(previewMedia.url)}
                  controls
                  autoPlay
                  style={{ width: '100%', maxHeight: '65vh', borderRadius: '8px', backgroundColor: '#000000' }}
                />
              ) : (
                <img
                  src={getFullMediaUrl(previewMedia.url)}
                  alt={previewMedia.originalName || previewMedia.fileName}
                  style={{ maxWidth: '100%', maxHeight: '65vh', borderRadius: '8px', objectFit: 'contain' }}
                />
              )}
            </div>

            {/* Modal Footer Actions */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                Format: {previewMedia.mimeType || previewMedia.mediaType}
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleDownloadMedia(previewMedia)}
                  style={{
                    padding: '8px 16px', backgroundColor: '#2563eb', color: '#ffffff',
                    border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <Download size={14} /> Download File
                </button>
                <button
                  onClick={() => setPreviewMedia(null)}
                  style={{
                    padding: '8px 16px', backgroundColor: '#334155', color: '#ffffff',
                    border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorageManagement;

